import React, { useState } from 'react';
import { loadEnglishDictionaries } from '../../loader/loadEnglishDictionaries';
import { ChoiceList } from '../widgets/ChoiceList';
import { englishAccentRules } from '../../ruleSets/accentRules';
import { InputType, getDefaultAccent, inputOptions, sampleText, spanishAccents } from './inputOptions';
import { speakPhonemes, stopEspeak } from '../../speak/speakPhonemes';
import { Expando } from '../widgets/Expando';
import { IpaOptions } from './IpaOptions';
import { RuleSet } from '../../rules/ruleSet';
import { ColumnAlign } from '../widgets/ColumnAlign';
import { HandleRules } from './handleRules';
import { SupportedAccent } from '../../ruleSets/allRules';
import { IpaKeyboard } from '../widgets/IpaKeyboard';
import { IpaButtons } from '../widgets/IpaButtons';
import { CheckBox } from '../widgets/CheckBox';
import { speakOnCtrlS } from '../widgets/SpeakOnCtrlS';

/** when an explicit width is needed for content */
const roughWidth = 580;

/** UI for input, input type, optionally running accent rules */
export function InOutRules() {
  const [handler] = useState(() => new HandleRules());
  const [englishOptions] = useState(Object.getOwnPropertyNames(englishAccentRules).filter(s => !s.includes('Dictionary ')));
  const [selectedInput, setSelectedInput] = useState<InputType>('IPA');
  const [inputText, setInputText] = useState(sampleText['IPA']);
  const [inputErrors, setInputErrors] = useState('');
  const [showIpa, setShowIpa] = useState(false);
  const [insert, setInsert] = useState<[string] | undefined>();
  const [editedInput, setEditedInput] = useState(false);
  const [outputText, setOutputText] = useState('');
  const [selectedAccent, setSelectedAccent] = useState<SupportedAccent>('none');
  const [resultText, setResultText] = useState('');
  const [asSpoken, setAsSpoken] = useState('');

  const [init, setInit] = useState(false);
  if (!init) {
    setInit(true);
    transformInput(inputText, selectedInput, selectedAccent);
    loadEnglishDictionaries();
  }

  /** user picked a different item from the input-type list */
  function changeSelectedInput(selectedInputType: string) {
    const newInputType = selectedInputType as InputType;
    setSelectedInput(newInputType);

    const switchingEnglish = selectedInput.includes('English (') && newInputType.includes('English (');
    // usually switch to new language's sample text,
    // but if we're switching English's & we edited the text, keep it as-is
    let changeInputType = true;
    if (editedInput && switchingEnglish) {
      changeInputType = false;
    }
    const newInputText = changeInputType ? sampleText[newInputType] : inputText;
    if (changeInputType) {
      setInputText(newInputText);
      setEditedInput(false);
    }
    // switch accent unless we're switching English
    const newAccent = switchingEnglish ? selectedAccent : getDefaultAccent(newInputType);
    setSelectedAccent(newAccent);

    transformInput(newInputText, newInputType, newAccent);
  }

  /** user changed the input text */
  function changeInputText(newText: string) {
    setInputText(newText);
    setEditedInput(true);
    transformInput(newText, selectedInput, selectedAccent);
  }

  function changeIpaRules(rule: RuleSet) {
    handler.setCustomIpaRule(rule);
    transformInput(inputText, selectedInput, selectedAccent);
  }

  /** user changed the selected accent */
  function changeAccent(newAccent: SupportedAccent) {
    setSelectedAccent(newAccent);
    transformInput(inputText, selectedInput, newAccent);
  }

  /** speak the results text taking into account the accent's length rules */
  function speakAccent() {
    const addPhonemeLength = handler.getAddPhonemeLength(selectedInput, selectedAccent);
    const { approxIpa } = speakPhonemes(resultText, undefined, addPhonemeLength);
    setAsSpoken(approxIpa);
  }
  function speakIpa() {
    const addPhonemeLength = handler.getAddPhonemeLength(selectedInput, selectedAccent);
    const { approxIpa } = speakPhonemes(outputText, undefined, addPhonemeLength);
    setAsSpoken(approxIpa);
  }

  /** update any UI that depends on input, input type, accent */
  function transformInput(input: string, inputType: InputType, accent: SupportedAccent) {
    const { ipa, afterRules, badLanguage } = handler.convert(input, inputType, accent);
    setOutputText(ipa);
    setResultText(afterRules);
    setInputErrors(badLanguage && badLanguage.length > 0 ? `unknown: ${badLanguage.join(', ')}` : '');
  }

  function changeShowKeyboard(checked: boolean) {
    setShowIpa(checked);
    document.getElementById('prime-input')?.focus();
  }

  const accentOptions = selectedInput === 'Spanish' ? spanishAccents
    : selectedInput.startsWith('English') ? englishOptions
      : null;
  const issuesLabel = `show issues ${inputErrors.length === 0 ? '(none found)' : selectedInput === 'IPA' ? '(issues detected, use substitution to fix)' : '(issues detected)'}`;
  const outputLabel = selectedInput === 'Spanish' ? 'basic pronunciation'
    : selectedInput.startsWith('English') ? 'dictionary output'
      : 'output';
  const resultLabel = selectedInput === 'Spanish' || selectedInput.startsWith('English') ? 'accent' : 'result';

  return (<>
    <ColumnAlign width={roughWidth}>{[
      <span key={1}>
        input:&nbsp;<ChoiceList items={inputOptions} selectedItem={selectedInput} onSelect={changeSelectedInput} />
      </span>,
      <span key={2}>
        {selectedInput !== 'IPA' ? <></> : <CheckBox checked={showIpa} onChange={changeShowKeyboard}>show IPA keyboard</CheckBox>}
      </span>
    ]}</ColumnAlign>
    {selectedInput === 'IPA'
      ? <>
        {showIpa ? <IpaButtons onClick={(s) => setInsert([s])} ipa={true} width={roughWidth} /> : <></>}
        <IpaKeyboard id='prime-input' cols={80} rows={5} onSpeak={speakIpa}
          value={inputText} onChange={changeInputText} insert={insert} />
      </>
      : <textarea cols={80} rows={5} spellCheck={false} value={inputText}
        onChange={(event) => changeInputText(event.target.value)}
        onKeyDown={e => speakOnCtrlS(e, speakAccent)} />
    }
    <br />
    <Expando label={issuesLabel}>{inputErrors}</Expando>
    <br /><br />

    <ColumnAlign width={roughWidth}>{[
      <span key={1}>{outputLabel}</span>,
      <span key={2}>
        <button onClick={() => speakIpa()}>speak output</button>
        &nbsp;&nbsp;&nbsp;
        <button onClick={() => stopEspeak()}>stop</button>
      </span>,
    ]}</ColumnAlign>
    <IpaKeyboard cols={80} rows={5} value={outputText} onChange={setOutputText} onSpeak={speakIpa} />

    {!accentOptions ? undefined
      : <>
        <br /><br />
        <ColumnAlign width={roughWidth}>{[
          <span key={1}>{resultLabel}</span>,
          <span key={2}>
            rules:&nbsp;
            <ChoiceList key={2} items={accentOptions} selectedItem={selectedAccent}
              onSelect={(a) => changeAccent(a as SupportedAccent)} />
          </span>,
          <span key={3}>
            <button onClick={() => speakAccent()}>speak accent</button>
            &nbsp;&nbsp;&nbsp;
            <button onClick={() => stopEspeak()}>stop</button>
          </span>,
        ]}</ColumnAlign>

        <IpaKeyboard cols={80} rows={5} value={resultText} onChange={setResultText} onSpeak={speakAccent} />
      </>
    }

    {selectedInput !== 'IPA' ? undefined
      : <><br /><IpaOptions ipa={inputText} asSpoken={asSpoken} setRules={changeIpaRules} /></>}
  </>);
}
